import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import {
  FiCheckCircle,
  FiChevronDown,
  FiMoreHorizontal,
  FiSearch,
  FiX,
} from "react-icons/fi";

import "./currency-picker.styles.scss";

export default function CurrencyPicker({
  value,
  onChange,
  currencies = [],
  commonCurrencyCodes = [
    "USD",
    "EUR",
    "GBP",
    "THB",
    "TRY",
    "JPY",
  ],
  label = "Currency",
  placeholder = "Select currency",
  disabled = false,
  showViewer = true,
  modalTitle = "Select currency",
}) {
  const [modalVisible, setModalVisible] =
    useState(false);

  const [search, setSearch] = useState("");

  const searchInputRef = useRef(null);

  const normalizedValue = String(
    value || ""
  ).toUpperCase();

  const selectedCurrency = useMemo(
    () =>
      currencies.find(
        (currency) =>
          currency.code === normalizedValue
      ) || null,
    [currencies, normalizedValue]
  );

  const commonCurrencies = useMemo(
    () =>
      commonCurrencyCodes
        .map((code) =>
          currencies.find(
            (currency) => currency.code === code
          )
        )
        .filter(Boolean),
    [currencies, commonCurrencyCodes]
  );

  const filteredCurrencies = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return currencies;

    return currencies.filter((currency) => {
      const code = String(
        currency.code || ""
      ).toLowerCase();

      const name = String(
        currency.name || ""
      ).toLowerCase();

      const symbol = String(
        currency.symbol || ""
      ).toLowerCase();

      return (
        code.includes(query) ||
        name.includes(query) ||
        symbol.includes(query)
      );
    });
  }, [currencies, search]);

  const closeModal = () => {
    setModalVisible(false);
    setSearch("");
  };

  const openModal = () => {
    if (disabled) return;

    setModalVisible(true);
  };

  const handleSelect = (currency) => {
    onChange?.(currency);
    closeModal();
  };

  useEffect(() => {
    if (!modalVisible) return;

    const focusTimeout = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 50);

    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      // Prevent InvoiceBuilder's Escape handler from
      // closing the entire builder as well.
      event.preventDefault();
      event.stopImmediatePropagation();

      closeModal();
    };

    window.addEventListener(
      "keydown",
      handleEscape,
      true
    );

    return () => {
      window.clearTimeout(focusTimeout);

      window.removeEventListener(
        "keydown",
        handleEscape,
        true
      );
    };
  }, [modalVisible]);

  const modal =
    modalVisible &&
    createPortal(
      <div
        className="currency-picker__modal-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            closeModal();
          }
        }}
      >
        <section
          className="currency-picker__modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="currency-picker-title"
        >
          <header className="currency-picker__modal-header">
            <h3 id="currency-picker-title">
              {modalTitle}
            </h3>

            <button
              type="button"
              onClick={closeModal}
              aria-label="Close currency picker"
            >
              <FiX />
            </button>
          </header>

          <div className="currency-picker__search">
            <FiSearch />

            <input
              ref={searchInputRef}
              type="search"
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search code, name or symbol"
            />
          </div>

          <div className="currency-picker__list">
            {filteredCurrencies.map((currency) => {
              const isSelected =
                currency.code === normalizedValue;

              return (
                <button
                  type="button"
                  key={currency.code}
                  className={
                    isSelected
                      ? "currency-picker__list-item currency-picker__list-item--selected"
                      : "currency-picker__list-item"
                  }
                  onClick={() =>
                    handleSelect(currency)
                  }
                >
                  <span className="currency-picker__list-left">
                    <span className="currency-picker__flag">
                      {currency.flag}
                    </span>

                    <span className="currency-picker__description">
                      <strong>
                        {currency.code} ·{" "}
                        {currency.symbol}
                      </strong>

                      <small>{currency.name}</small>
                    </span>
                  </span>

                  {isSelected && <FiCheckCircle />}
                </button>
              );
            })}

            {!filteredCurrencies.length && (
              <div className="currency-picker__empty">
                No currency found
              </div>
            )}
          </div>
        </section>
      </div>,
      document.body
    );

  return (
    <div className="currency-picker">
      {label && (
        <span className="currency-picker__label">
          {label}
        </span>
      )}

      {showViewer && (
        <button
          type="button"
          className="currency-picker__viewer"
          disabled={disabled}
          onClick={openModal}
          aria-haspopup="dialog"
          aria-expanded={modalVisible}
        >
          {selectedCurrency ? (
            <span className="currency-picker__viewer-left">
              <span className="currency-picker__flag">
                {selectedCurrency.flag}
              </span>

              <span className="currency-picker__description">
                <strong>
                  {selectedCurrency.code} ·{" "}
                  {selectedCurrency.symbol}
                </strong>

                <small>
                  {selectedCurrency.name}
                </small>
              </span>
            </span>
          ) : (
            <span className="currency-picker__placeholder">
              {placeholder}
            </span>
          )}

          {!disabled && <FiChevronDown />}
        </button>
      )}

      <div className="currency-picker__common-row">
        {commonCurrencies.map((currency) => {
          const isSelected =
            currency.code === normalizedValue;

          return (
            <button
              type="button"
              key={currency.code}
              className={
                isSelected
                  ? "currency-picker__chip currency-picker__chip--selected"
                  : "currency-picker__chip"
              }
              disabled={disabled}
              onClick={() => handleSelect(currency)}
              aria-pressed={isSelected}
            >
              <span>{currency.flag}</span>
              {currency.code}
            </button>
          );
        })}

        <button
          type="button"
          className="currency-picker__more"
          disabled={disabled}
          onClick={openModal}
          aria-label="Show all currencies"
        >
          <FiMoreHorizontal />
        </button>
      </div>

      {modal}
    </div>
  );
}